import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Database,
  Brain,
  BarChart,
  Target,
  Shield,
  BarChart2,
  Calculator,
  FileText,
  TrendingUp,
  ChevronRight
} from 'lucide-react';
import SEOHead from '../components/SEO/SEOHead';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEOHead 
        title="About PricePatent | Patent Valuation Methodology & AI Technology"
        description="Learn about PricePatent's AI-powered patent valuation methodology. Discover how we combine machine learning, market data, and IP analytics to provide accurate patent assessments for professionals and investors."
        keywords="patent valuation methodology, AI patent valuation, intellectual property methodology, patent assessment technology, IP valuation process, patent analytics methodology"
        ogTitle="About PricePatent - Patent Valuation Methodology & Technology"
        ogDescription="Discover the science behind AI-powered patent valuation. Learn our methodology for accurate IP assessments and analytics."
        structuredData={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About PricePatent",
          "description": "Information about PricePatent's patent valuation methodology and AI technology",
          "mainEntity": {
            "@type": "Organization",
            "name": "PricePatent",
            "description": "AI-powered patent valuation platform using advanced machine learning for IP analytics",
            "specialty": ["Patent Valuation", "IP Analytics", "Intellectual Property Assessment"],
            "knowsAbout": [
              "Patent Valuation Methodology",
              "Income Method Valuation", 
              "AI-powered IP Analysis",
              "Patent Licensing",
              "IP Monetization"
            ]
          }
        }}
      />
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-16">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/3">
            <svg width="800" height="800" viewBox="0 0 800 800" fill="none">
              <circle cx="400" cy="400" r="400" fill="url(#heroGradient)" fillOpacity="0.1" />
              <defs>
                <radialGradient id="heroGradient" cx="0" cy="0" r="1">
                  <stop stopColor="#2563EB" />
                  <stop offset="1" stopColor="#2563EB" stopOpacity="0" />
                </radialGradient>
              </defs>
            </svg>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center bg-white/50 backdrop-blur-sm rounded-2xl p-8">
            <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-full text-blue-600 text-sm font-medium mb-6">
              <Database className="h-4 w-4 mr-2" />
              Our Methodology
            </div>
            <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
              <span className="block">Our Patent Valuation</span>
              <span className="block mt-2 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent leading-normal pb-2">Methodology</span>
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600">
              A comprehensive explanation of how we calculate patent values using the Income Method, combining AI-powered analysis with traditional valuation principles.
            </p>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Overview</h2>
              <div className="ml-4 flex-grow h-px bg-gradient-to-r from-blue-600/20 to-transparent"></div>
            </div>
            <div className="space-y-8">
              <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                    <Calculator className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="ml-4 text-xl font-semibold text-gray-900">The Income Method</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Our patent valuation methodology is based on the Income Method, a widely accepted approach in the field of intellectual property valuation. This method estimates the value of a patent by calculating the present value of the future economic benefits it is expected to generate.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
                    <Brain className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="ml-4 text-xl font-semibold text-gray-900">Enhanced with AI</h3>
                </div>
                <p className="text-gray-600 leading-relaxed mb-6">
                  We enhance this traditional approach with AI-powered analysis and comprehensive market data to provide more accurate and objective valuations. Our system combines:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full"></div>
                      <span className="ml-3 text-gray-700">Advanced machine learning algorithms</span>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full"></div>
                      <span className="ml-3 text-gray-700">Comprehensive patent databases</span>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full"></div>
                      <span className="ml-3 text-gray-700">Market and financial data analysis</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Core Components Section */}
      <div className="bg-gradient-to-br from-gray-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Core Components</h2>
            <div className="ml-4 flex-grow h-px bg-gradient-to-r from-blue-600/20 to-transparent"></div>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center">
                  <Database className="h-7 w-7 text-white" />
                </div>
                <h3 className="ml-4 text-2xl font-semibold text-gray-900">Data Collection</h3>
              </div>
              <div className="space-y-6">
                <p className="text-gray-600 text-lg">
                  We collect and analyze data from multiple sources:
                </p>
                <div className="grid grid-cols-1 gap-4">
                  {[
                    'Patent databases (USPTO, EPO, WIPO)',
                    'Financial statements of patent-holding companies',
                    'Market research reports',
                    'Industry-specific data'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center bg-blue-50 rounded-lg p-4">
                      <ChevronRight className="h-5 w-5 text-blue-600" />
                      <span className="ml-3 text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                  <Brain className="h-7 w-7 text-white" />
                </div>
                <h3 className="ml-4 text-2xl font-semibold text-gray-900">AI Analysis</h3>
              </div>
              <div className="space-y-6">
                <p className="text-gray-600 text-lg">
                  Our AI system performs several key analyses:
                </p>
                <div className="grid grid-cols-1 gap-4">
                  {[
                    'Patent quality assessment',
                    'Market potential evaluation',
                    'Competitive landscape analysis',
                    'Technology trend forecasting'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center bg-purple-50 rounded-lg p-4">
                      <ChevronRight className="h-5 w-5 text-purple-600" />
                      <span className="ml-3 text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Valuation Process Section */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Valuation Process</h2>
            <div className="ml-4 flex-grow h-px bg-gradient-to-r from-blue-600/20 to-transparent"></div>
          </div>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-emerald-500 via-amber-500 to-indigo-500"></div>
            
            {/* Process Steps */}
            <div className="space-y-16">
              {/* Step 1 */}
              <div className="relative">
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center z-10">
                    <div className="text-2xl font-bold text-white">1</div>
                  </div>
                  <div className="ml-8 flex-grow">
                    <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-center mb-4">
                        <Calculator className="h-6 w-6 text-emerald-600" />
                        <h3 className="ml-3 text-2xl font-semibold text-gray-900">Industry Profit Analysis</h3>
                      </div>
                      <p className="text-gray-600 text-lg leading-relaxed">
                        We estimate the total profit attributable to all patents within the same industry for a particular year. This is calculated using financial data from patent-holding companies and market research reports.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative">
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center z-10">
                    <div className="text-2xl font-bold text-white">2</div>
                  </div>
                  <div className="ml-8 flex-grow">
                    <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-center mb-4">
                        <BarChart className="h-6 w-6 text-amber-600" />
                        <h3 className="ml-3 text-2xl font-semibold text-gray-900">Patent Score Distribution</h3>
                      </div>
                      <p className="text-gray-600 text-lg leading-relaxed mb-6">
                        The total industry profit is distributed among patents based on their individual scores. These scores are determined by factors such as:
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                          'Number of citations',
                          'Geographical coverage',
                          'Legal status and maintenance',
                          'Technology relevance'
                        ].map((item, index) => (
                          <div key={index} className="flex items-center bg-amber-50 rounded-lg p-4">
                            <ChevronRight className="h-5 w-5 text-amber-600" />
                            <span className="ml-3 text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative">
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full flex items-center justify-center z-10">
                    <div className="text-2xl font-bold text-white">3</div>
                  </div>
                  <div className="ml-8 flex-grow">
                    <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-center mb-4">
                        <TrendingUp className="h-6 w-6 text-indigo-600" />
                        <h3 className="ml-3 text-2xl font-semibold text-gray-900">NPV Calculation</h3>
                      </div>
                      <p className="text-gray-600 text-lg leading-relaxed mb-6">
                        We calculate the Net Present Value (NPV) by:
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                          'Projecting annual profits over the patent\'s remaining life',
                          'Applying appropriate discount rates',
                          'Considering market growth and technology trends',
                          'Adjusting for risk factors'
                        ].map((item, index) => (
                          <div key={index} className="flex items-center bg-indigo-50 rounded-lg p-4">
                            <ChevronRight className="h-5 w-5 text-indigo-600" />
                            <span className="ml-3 text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Output Section */}
      <div className="bg-gradient-to-br from-gray-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Valuation Output</h2>
            <div className="ml-4 flex-grow h-px bg-gradient-to-r from-blue-600/20 to-transparent"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-r from-rose-500 to-red-500 rounded-xl flex items-center justify-center">
                  <FileText className="h-7 w-7 text-white" />
                </div>
                <div className="ml-6">
                  <h3 className="text-2xl font-semibold text-gray-900">Comprehensive Report</h3>
                  <p className="mt-2 text-gray-500">Complete valuation analysis and insights</p>
                </div>
              </div>
              <div className="space-y-4">
                {[
                  'Patent value estimate with confidence intervals',
                  'Detailed breakdown of contributing factors',
                  'Market analysis and trends',
                  'Comparable patent valuations',
                  'Risk assessment and sensitivity analysis'
                ].map((item, index) => (
                  <div key={index} className="flex items-center bg-rose-50 rounded-lg p-4">
                    <ChevronRight className="h-5 w-5 text-rose-600" />
                    <span className="ml-3 text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-sm p-8 text-white">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
                  <Target className="h-7 w-7 text-white" />
                </div>
                <div className="ml-6">
                  <h3 className="text-2xl font-semibold">Key Benefits</h3>
                  <p className="mt-2 text-blue-100">Why choose our valuation reports</p>
                </div>
              </div>
              <div className="space-y-4">
                {[
                  'AI-powered accuracy and consistency',
                  'Comprehensive market insights',
                  'Clear, actionable recommendations',
                  'Regular updates and monitoring',
                  'Expert support and consultation'
                ].map((item, index) => (
                  <div key={index} className="flex items-center bg-white/10 rounded-lg p-4">
                    <ChevronRight className="h-5 w-5 text-white" />
                    <span className="ml-3">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Ready to see our methodology in action?
            </h2>
            <p className="mt-4 text-xl text-blue-100">
              Try our demo or view sample data to see detailed valuation results.
            </p>
            <div className="mt-8 flex justify-center space-x-4">
              <Link
                to="/demo"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-blue-600 bg-white hover:bg-blue-50 transition-colors duration-300"
              >
                Try Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/sample-report"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-700 hover:bg-blue-800 transition-colors duration-300"
              >
                View Sample Data
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage; 